/**
 * useCheckoutFields
 *
 * Single /checkout-fields fetch drives both field lists and required-flag maps.
 * Replaces the two separate fetchBillingFieldMeta / fetchShippingFieldMeta calls.
 *
 * Endpoints consumed (plugin routes — base resolved by utils/pluginApi.ts):
 *   GET /countries
 *   GET /reps            — client plugin build only; 404 elsewhere → no reps
 *   GET /order-types     — client plugin build only; 404 elsewhere → none
 *   GET /checkout-fields   ← one fetch, all groups
 */
import { useState, useEffect } from "react";
import {
  fetchWpCountries,
  fetchWpReps,
  fetchWpOrderTypes,
  type WpCountry,
  type WpRep,
  type WpOrderTypeOption,
} from "../services/api";
import { pluginApiUrl } from "../utils/pluginApi";

export type { WpCountry, WpRep, WpOrderTypeOption };

// ── Public field descriptor ───────────────────────────────────────────────────

export interface CheckoutField {
  /** Form-state key (short).  e.g. "first_name", "billing_field_type". */
  key: string;
  /** Original WooCommerce key.  e.g. "billing_first_name". */
  wcKey: string;
  label: string;
  required: boolean;
  /** WC field type: "text" | "select" | "country" | "state" | "tel" | "email" | "textarea". */
  type: string;
  priority: number;
  /**
   * Options for select fields, as registered on the field itself. Present for
   * e.g. project_rep, whose option map is the SAME list the storefront
   * checkout renders — and which is not the same set as /reps (that endpoint
   * only returns users holding a rep role). Rendering a select from any other
   * source risks a stored value matching no option, which renders blank while
   * still reading as filled. The empty-key placeholder entry is dropped; the
   * card supplies its own.
   */
  options?: { value: string; label: string }[];
  /** Renderer hint for special dropdowns. */
  kind?: "rep" | "orderType" | "country" | "state";
}

// ── Key normalisation ─────────────────────────────────────────────────────────

// THWCFE custom fields whose names START with "billing_" but must keep the full
// name in form state — stripping the prefix would make them ambiguous / break
// the downstream __BULK_ADDR__ consumer that looks for these exact keys.
const BILLING_KEEP_PREFIX = new Set(["billing_field_type", "billing_project"]);

// THWCFE sometimes registers "<group>_company_name" instead of the standard
// "<group>_company". Normalise BOTH groups so form state always uses "company".
// Billing was missing here: "billing_company_name" stripped to "company_name",
// which matches nothing in AddressDict — so the Company Name box rendered with
// the right label but never prefilled from cart.billing_address.company, and
// placeOrder read billing.company as "" and saved no company on the order.
const COMPANY_KEY_REMAP: Record<string, string> = {
  billing_company_name: "company",
  shipping_company_name: "company",
};

function formKey(wcKey: string, group: "billing" | "shipping"): string {
  if (group === "billing" && BILLING_KEEP_PREFIX.has(wcKey)) return wcKey;
  const remap = COMPANY_KEY_REMAP[wcKey];
  if (remap) return remap;
  const prefix = `${group}_`;
  const stripped = wcKey.startsWith(prefix)
    ? wcKey.slice(prefix.length)
    : wcKey;
  // Catch any other "<group>_company_name" spelling the theme may register.
  return stripped === "company_name" ? "company" : stripped;
}

// ── kind derivation ───────────────────────────────────────────────────────────

function kindFor(wcKey: string, type: string): CheckoutField["kind"] {
  if (type === "country") return "country";
  if (type === "state") return "state";
  if (wcKey === "project_rep" || wcKey === "billing_project_rep") return "rep";
  if (wcKey === "billing_field_type") return "orderType";
  return undefined;
}

// ── Raw API shape ─────────────────────────────────────────────────────────────

interface RawWcField {
  label?: string;
  required?: boolean;
  type?: string;
  priority?: number;
  options?: Record<string, string>;
}

// ── Group parser ──────────────────────────────────────────────────────────────

/** WC serialises select options as value → label. Drop the blank placeholder. */
function parseOptions(
  raw: Record<string, string> | undefined,
): { value: string; label: string }[] | undefined {
  if (!raw) return undefined;
  const out = Object.entries(raw)
    .filter(([value]) => value !== "")
    .map(([value, label]) => ({ value, label: String(label) }));
  return out.length ? out : undefined;
}

function parseGroup(
  raw: Record<string, RawWcField>,
  group: "billing" | "shipping",
): CheckoutField[] {
  return Object.entries(raw)
    .map(([wcKey, cfg]) => ({
      key: formKey(wcKey, group),
      wcKey,
      label: cfg.label ?? wcKey,
      required: cfg.required ?? false,
      type: cfg.type ?? "text",
      priority: cfg.priority ?? 999,
      options: parseOptions(cfg.options),
      kind: kindFor(wcKey, cfg.type ?? ""),
    }))
    .sort((a, b) => a.priority - b.priority);
}

// ── Fallbacks (used when the fetch fails) ─────────────────────────────────────
//
// WooCommerce's own default checkout fields — nothing store-specific. Used
// while /checkout-fields loads and whenever it fails (a firewall block, an
// older plugin). A store's custom fields (Order Type, Project Name, Your Rep,
// …) appear only when that store's live /checkout-fields lists them; a
// fallback that assumed one store's form made every other store's shoppers
// fill in fields their checkout doesn't have.

const BILLING_FIELDS_FALLBACK: CheckoutField[] = [
  {
    key: "first_name",
    wcKey: "billing_first_name",
    label: "First name",
    required: true,
    type: "text",
    priority: 10,
  },
  {
    key: "last_name",
    wcKey: "billing_last_name",
    label: "Last name",
    required: true,
    type: "text",
    priority: 20,
  },
  {
    key: "company",
    wcKey: "billing_company",
    label: "Company name",
    required: false,
    type: "text",
    priority: 30,
  },
  {
    key: "country",
    wcKey: "billing_country",
    label: "Country",
    required: true,
    type: "country",
    priority: 40,
    kind: "country",
  },
  {
    key: "address_1",
    wcKey: "billing_address_1",
    label: "Address 1",
    required: true,
    type: "text",
    priority: 50,
  },
  {
    key: "address_2",
    wcKey: "billing_address_2",
    label: "Address 2",
    required: false,
    type: "text",
    priority: 60,
  },
  {
    key: "city",
    wcKey: "billing_city",
    label: "City",
    required: true,
    type: "text",
    priority: 70,
  },
  {
    key: "state",
    wcKey: "billing_state",
    label: "State",
    required: true,
    type: "state",
    priority: 80,
    kind: "state",
  },
  {
    key: "postcode",
    wcKey: "billing_postcode",
    label: "Postcode",
    required: true,
    type: "text",
    priority: 90,
  },
  {
    key: "phone",
    wcKey: "billing_phone",
    label: "Phone",
    required: false,
    type: "tel",
    priority: 100,
  },
  {
    key: "email",
    wcKey: "billing_email",
    label: "Email",
    required: true,
    type: "email",
    priority: 110,
  },
];

const SHIPPING_FIELDS_FALLBACK: CheckoutField[] = [
  {
    key: "first_name",
    wcKey: "shipping_first_name",
    label: "First name",
    required: true,
    type: "text",
    priority: 10,
  },
  {
    key: "last_name",
    wcKey: "shipping_last_name",
    label: "Last name",
    required: true,
    type: "text",
    priority: 20,
  },
  {
    key: "company",
    wcKey: "shipping_company",
    label: "Company name",
    required: false,
    type: "text",
    priority: 30,
  },
  {
    key: "country",
    wcKey: "shipping_country",
    label: "Country",
    required: true,
    type: "country",
    priority: 40,
    kind: "country",
  },
  {
    key: "address_1",
    wcKey: "shipping_address_1",
    label: "Address 1",
    required: true,
    type: "text",
    priority: 50,
  },
  {
    key: "address_2",
    wcKey: "shipping_address_2",
    label: "Address 2",
    required: false,
    type: "text",
    priority: 60,
  },
  {
    key: "city",
    wcKey: "shipping_city",
    label: "City",
    required: true,
    type: "text",
    priority: 70,
  },
  {
    key: "state",
    wcKey: "shipping_state",
    label: "State",
    required: false,
    type: "state",
    priority: 80,
    kind: "state",
  },
  {
    key: "postcode",
    wcKey: "shipping_postcode",
    label: "Postcode",
    required: true,
    type: "text",
    priority: 90,
  },
  {
    key: "order_notes",
    wcKey: "order_comments",
    label: "Order notes",
    required: false,
    type: "textarea",
    priority: 9999,
  },
];

/** Required flags for the fallback lists, derived from them so the two can't drift. */
function requiredMap(
  fields: CheckoutField[],
): Record<string, { required?: boolean }> {
  return Object.fromEntries(
    fields.map((f) => [f.key, { required: f.required }]),
  );
}

const BILLING_REQUIRED_FALLBACK = requiredMap(BILLING_FIELDS_FALLBACK);
const SHIPPING_REQUIRED_FALLBACK = requiredMap(SHIPPING_FIELDS_FALLBACK);

// ── Single fetch ──────────────────────────────────────────────────────────────

interface FieldMeta {
  billingFields: CheckoutField[];
  shippingFields: CheckoutField[];
  billingFieldOverrides: Record<string, { required?: boolean }>;
  shippingFieldOverrides: Record<string, { required?: boolean }>;
}

async function fetchFieldMeta(wpBase: string): Promise<FieldMeta> {
  const fallback: FieldMeta = {
    billingFields: BILLING_FIELDS_FALLBACK,
    shippingFields: SHIPPING_FIELDS_FALLBACK,
    billingFieldOverrides: BILLING_REQUIRED_FALLBACK,
    shippingFieldOverrides: SHIPPING_REQUIRED_FALLBACK,
  };

  try {
    const res = await fetch(pluginApiUrl(wpBase, "/checkout-fields"));
    if (!res.ok) return fallback;

    const data: Record<string, Record<string, RawWcField>> = await res.json();

    const billingFields = parseGroup(data.billing ?? {}, "billing");
    const shippingCore = parseGroup(data.shipping ?? {}, "shipping");

    // WC stores order_comments in the "order" group; append it to shipping fields.
    const orderRaw = data.order ?? {};
    const orderNotes: CheckoutField | null = orderRaw.order_comments
      ? {
          key: "order_notes",
          wcKey: "order_comments",
          label: orderRaw.order_comments.label ?? "Order notes",
          required: false,
          type: "textarea",
          priority: 9999,
        }
      : null;

    const shippingFields = orderNotes
      ? [...shippingCore, orderNotes]
      : shippingCore;

    if (!billingFields.length && !shippingFields.length) return fallback;

    // Derive override maps from parsed lists (backward-compat for other consumers).
    const billingFieldOverrides: Record<string, { required?: boolean }> = {};
    for (const f of billingFields)
      billingFieldOverrides[f.key] = { required: f.required };

    const shippingFieldOverrides: Record<string, { required?: boolean }> = {};
    for (const f of shippingFields)
      shippingFieldOverrides[f.key] = { required: f.required };

    return {
      billingFields: billingFields.length
        ? billingFields
        : BILLING_FIELDS_FALLBACK,
      shippingFields: shippingFields.length
        ? shippingFields
        : SHIPPING_FIELDS_FALLBACK,
      billingFieldOverrides,
      shippingFieldOverrides,
    };
  } catch {
    return fallback;
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface CheckoutFieldsData {
  countries: WpCountry[];
  reps: WpRep[];
  orderTypeOptions: WpOrderTypeOption[];
  /** Full API-driven billing field list, sorted by WC priority. */
  billingFields: CheckoutField[];
  /** Full API-driven shipping field list, sorted by WC priority. */
  shippingFields: CheckoutField[];
  /** Required flags keyed by short form key — backward-compat for AddressForm. */
  billingFieldOverrides: Record<string, { required?: boolean }>;
  shippingFieldOverrides: Record<string, { required?: boolean }>;
  isLoading: boolean;
  error: string | null;
}

export function useCheckoutFields(wpBase: string): CheckoutFieldsData {
  const [countries, setCountries] = useState<WpCountry[]>([]);
  const [reps, setReps] = useState<WpRep[]>([]);
  const [orderTypeOptions, setOrderTypeOptions] = useState<WpOrderTypeOption[]>(
    [],
  );
  const [billingFields, setBillingFields] = useState<CheckoutField[]>(
    BILLING_FIELDS_FALLBACK,
  );
  const [shippingFields, setShippingFields] = useState<CheckoutField[]>(
    SHIPPING_FIELDS_FALLBACK,
  );
  const [billingFieldOverrides, setBillingFieldOverrides] = useState(
    BILLING_REQUIRED_FALLBACK,
  );
  const [shippingFieldOverrides, setShippingFieldOverrides] = useState(
    SHIPPING_REQUIRED_FALLBACK,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!wpBase) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    Promise.allSettled([
      fetchWpCountries(wpBase),
      fetchWpReps(wpBase),
      fetchWpOrderTypes(wpBase),
      fetchFieldMeta(wpBase),
    ])
      .then(([c, r, ot, meta]) => {
        if (cancelled) return;
        setCountries(c.status === "fulfilled" ? c.value : []);
        setReps(r.status === "fulfilled" ? r.value : []);
        setOrderTypeOptions(ot.status === "fulfilled" ? ot.value : []);
        const metaValue =
          meta.status === "fulfilled"
            ? meta.value
            : {
                billingFields: BILLING_FIELDS_FALLBACK,
                shippingFields: SHIPPING_FIELDS_FALLBACK,
                billingFieldOverrides: BILLING_REQUIRED_FALLBACK,
                shippingFieldOverrides: SHIPPING_REQUIRED_FALLBACK,
              };

        // ── billing_field_type recovery ──────────────────────────────────
        // THWCFE evaluates conditional-display rules when its
        // woocommerce_checkout_fields hook runs. In the REST context (fresh
        // session, empty cart) the Order Type field's condition can evaluate
        // false, so it's stripped from /checkout-fields even though the real
        // checkout page renders it. /order-types reads thwcfe_sections
        // directly and bypasses that, so a non-empty options list is proof
        // the site uses the field — inject it if the parsed list lacks it.
        const orderTypes = ot.status === "fulfilled" ? ot.value : [];
        let billingFields = metaValue.billingFields;
        let billingFieldOverrides = metaValue.billingFieldOverrides;
        if (
          orderTypes.length > 0 &&
          !billingFields.some((f) => f.key === "billing_field_type")
        ) {
          billingFields = [
            ...billingFields,
            {
              key: "billing_field_type",
              wcKey: "billing_field_type",
              label: "Order Type",
              required: true,
              type: "select",
              priority: 91, // matches THWCFE's data-priority on the live form
              kind: "orderType" as const,
            },
          ].sort((a, b) => a.priority - b.priority);
          billingFieldOverrides = {
            ...billingFieldOverrides,
            billing_field_type: { required: true },
          };
        }

        setBillingFields(billingFields);
        setShippingFields(metaValue.shippingFields);
        setBillingFieldOverrides(billingFieldOverrides);
        setShippingFieldOverrides(metaValue.shippingFieldOverrides);
        if (r.status === "rejected")
          console.warn(
            "[useCheckoutFields] reps fetch failed — no reps available:",
            r.reason,
          );
        if (ot.status === "rejected")
          console.warn(
            "[useCheckoutFields] order-types fetch failed — no order types available:",
            ot.reason,
          );
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[useCheckoutFields] fetch failed:", err);
        setError(err?.message ?? "Failed to load checkout fields");
        // State stays as fallback initial values — forms remain functional.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [wpBase]);

  return {
    countries,
    reps,
    orderTypeOptions,
    billingFields,
    shippingFields,
    billingFieldOverrides,
    shippingFieldOverrides,
    isLoading,
    error,
  };
}
